import {
  Component,
  OnDestroy,
  TemplateRef,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { ButtonDirective } from "primeng/button";

import { quoteStatus } from "../../quote-form/quote-status.const";
import { QuoteRepository } from "../../quote.repository";
import { Router } from "@angular/router";
import { Quote } from "../../quote.type";
import { SentryErrorHandler } from "../../../shared/error-handler/sentry-error-handler";
import { QuoteEmail } from "../../models/email.model";
import { role } from "../../../user/role.const";
import { NotifyService } from "../../../notify/notify.service";
import { TOAST } from "../../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../../shared/backend/backend-error.pipe";
import { IRequestInfo } from "../../../shared/request-info/request-info.interface";
import { requestInfoType } from "../../../shared/request-info/request-info-type.const";
import { Client } from "../../../client/client.model";
import { DateRange } from "app/shared/date/date-range.model";
import { NgxPermissionsModule } from "ngx-permissions";
import { QuoteDeclineFormComponent } from "../../quote-decline-form/quote-decline-form.component";
import { TableSelectionBarComponent } from "../../../ui/table-selection-bar/table-selection-bar.component";

@Component({
  selector: "app-quote-list-batch-actions",
  templateUrl: "./quote-list-batch-actions.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    NgxPermissionsModule,
    QuoteDeclineFormComponent,
    TableSelectionBarComponent,
  ],
})
export class QuoteListBatchActionsComponent implements OnDestroy {
  private quoteRepository = inject(QuoteRepository);
  private notifyService = inject(NotifyService);
  private router = inject(Router);

  readonly selectedQuotes = input<Quote[]>([]);
  readonly totalCount = input(0);
  readonly client = input<Client>(undefined);
  readonly clearSelection = output();
  readonly selectAll = output();
  readonly selectionBar = viewChild<TemplateRef<unknown>>("selectionBar");
  readonly batchSendComplete = output();
  readonly batchDeleteComplete = output();
  readonly batchReplicateComplete = output();
  readonly selectionChange = output<Quote[]>();
  readonly toggleEditor = output();
  readonly requestInfoChange = output<{
    key: number;
    value: IRequestInfo;
  }>();

  public declineFormVisible = false;
  public role = role;
  public quotesToSelect: Quote[] = [];
  private subscription = new Subscription();
  private sentry = inject(SentryErrorHandler);
  private isBatchActionCompleted = true;

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public addNewClick() {
    this.router.navigate(["/quote/", 0], {
      state: { clientIds: [this.client()?.getId()].filter(Boolean) },
    });
  }

  public batchSend(event: {
    validityDates: DateRange;
    quoteEmail: QuoteEmail;
  }) {
    let requestIndex = 0;
    this.isBatchActionCompleted = false;

    const sendQuote = (quote: Quote) => {
      this.subscription.add(
        this.quoteRepository.sendQuote(quote.getId()).subscribe({
          next: (success) => {
            this.emitRequestInfo(
              quote.getId(),
              requestInfoType.success,
              "sent",
            );
            sendLoop(requestIndex++);
          },
          error: (err) => {
            this.sentry.handleError(err, { quoteOnBatchSend: quote });
            const error = err.error ? err.error.message : "error";
            this.emitRequestInfo(quote.getId(), requestInfoType.error, error);
            sendLoop(requestIndex++);
          },
        }),
      );
    };

    const updateAndSendQuote = (quote: Quote) => {
      const payload = {
        discriminator: quote.getQuoteType(),
      };

      payload["email"] = {
        to: event.quoteEmail.getTo(),
        cc: event.quoteEmail.getCc(),
        bcc: event.quoteEmail.getBcc(),
        subject: event.quoteEmail.getSubject(),
        message: event.quoteEmail.getMessage(),
        attachments: event.quoteEmail.getAttachments(),
      };
      payload["validFrom"] = event.validityDates.getStartDateString();
      payload["validTo"] = event.validityDates.getEndDateString();

      this.subscription.add(
        this.quoteRepository.patchQuote(quote.getId(), payload).subscribe({
          next: (pqtchedQuote) => {
            if (Object.prototype.hasOwnProperty.call(payload, "email")) {
              quote.setEmail(event.quoteEmail);
            }
            sendQuote(quote);
          },
          error: (err) => {
            this.sentry.handleError(err, { quoteOnBatchUpdate: quote });
            const error = err.error;
            let errorStr = error ? error.message : "error";

            if (error.errors.children) {
              // formatting for tooltip
              const errArr = <string[]>(
                new BackendErrorPipe().transform(error, "array")
              );
              errorStr = errArr.map((e, i) => (i > 0 ? "\n" + e : "" + e));
            }

            this.emitRequestInfo(
              quote.getId(),
              requestInfoType.error,
              errorStr,
            );
            sendLoop(requestIndex++);
          },
        }),
      );
    };

    const sendLoop = (id: number) => {
      if (requestIndex < this.selectedQuotes().length) {
        const quote = this.selectedQuotes()[requestIndex];
        this.emitRequestInfo(quote.getId(), requestInfoType.pending, "pending");

        if (quote.getStatus() !== quoteStatus.draft) {
          this.emitRequestInfo(
            quote.getId(),
            requestInfoType.skipped,
            "quote already sent",
          );
          sendLoop(requestIndex++);
        }

        if (quote && !quote.isValid()) {
          this.emitRequestInfo(
            quote.getId(),
            requestInfoType.skipped,
            "skipped, quote is not valid",
          );
          sendLoop(requestIndex++);
        }

        if (quote.getStatus() === quoteStatus.draft) {
          updateAndSendQuote(quote);
        }
      }

      if (requestIndex === this.selectedQuotes().length) {
        this.isBatchActionCompleted = true;
        this.selectionChange.emit([]);
        this.notifyService.toast(TOAST.INFO, "Batch send complete");
        this.batchSendComplete.emit();
      }
    };
    sendLoop(requestIndex);
  }

  public batchReplicate(keepTotalPrice = false) {
    let requestIndex = 0;
    this.isBatchActionCompleted = false;
    this.quotesToSelect = [];

    const replicateLoop = (id: number, keepTotalPriceinLoop: boolean) => {
      if (requestIndex < this.selectedQuotes().length) {
        const quote = this.selectedQuotes()[requestIndex];
        this.emitRequestInfo(quote.getId(), requestInfoType.pending, "pending");

        if (quote.getStatus() === quoteStatus.draft) {
          this.emitRequestInfo(
            quote.getId(),
            requestInfoType.skipped,
            "skipped, quote was not sent",
          );
          replicateLoop(requestIndex++, keepTotalPriceinLoop);
        }

        if (quote.getStatus() !== quoteStatus.draft) {
          this.subscription.add(
            this.quoteRepository
              .replicateQuote(quote.getId(), keepTotalPriceinLoop)
              .subscribe({
                next: (replicatedQuote) => {
                  this.emitRequestInfo(
                    quote.getId(),
                    requestInfoType.success,
                    quote.getStatus(),
                  );
                  this.quotesToSelect.push(replicatedQuote);
                  replicateLoop(requestIndex++, keepTotalPrice);
                },
                error: (err) => {
                  this.sentry.handleError(err, { quoteOnReplication: quote });
                  const error = err.error ? err.error.message : "error";
                  this.emitRequestInfo(
                    quote.getId(),
                    requestInfoType.error,
                    error,
                  );
                  replicateLoop(requestIndex++, keepTotalPrice);
                },
              }),
          );
        }
      }

      if (requestIndex === this.selectedQuotes().length) {
        this.isBatchActionCompleted = true;
        this.selectionChange.emit(this.quotesToSelect);
        this.notifyService.toast(TOAST.INFO, "Batch replication complete");
        this.batchReplicateComplete.emit();
      }
    };

    replicateLoop(requestIndex, keepTotalPrice);
  }

  public batchDelete() {
    let requestIndex = 0;
    this.isBatchActionCompleted = false;
    const selected = [...this.selectedQuotes()];

    const deleteLoop = (id: number) => {
      if (requestIndex < selected.length) {
        const quote = selected[requestIndex];

        if (quote.getStatus() !== quoteStatus.draft) {
          deleteLoop(requestIndex++);
        }

        if (quote.getStatus() === quoteStatus.draft) {
          this.subscription.add(
            this.quoteRepository.deleteQuote(quote.getId()).subscribe({
              next: (deletedQuote) => {
                deleteLoop(requestIndex++);
              },
              error: (err) => {
                this.sentry.handleError(err, { quoteOnDelete: quote });
                this.notifyService.toast(
                  TOAST.ERROR,
                  "Delete error",
                  new BackendErrorPipe().transform(err),
                );
                deleteLoop(requestIndex++);
              },
            }),
          );
        }
      }

      if (requestIndex === selected.length) {
        this.isBatchActionCompleted = true;
        this.selectionChange.emit([]);
        this.notifyService.toast(TOAST.INFO, "Batch delete complete");
        this.batchDeleteComplete.emit();
      }
    };

    this.subscription.add(
      this.notifyService
        .confirm("Are you sure you want to delete selected quotes?", "Delete", {
          destructive: true,
        })
        .subscribe({
          next: () => {
            deleteLoop(requestIndex);
          },
          error: () => {
            this.isBatchActionCompleted = true;
          },
        }),
    );
  }

  public batchAccept() {
    let requestIndex = 0;
    this.isBatchActionCompleted = false;
    const selected = [...this.selectedQuotes()];

    const statusLoop = (id: number) => {
      if (requestIndex < selected.length) {
        const quote = selected[requestIndex];

        if (
          quote.getStatus() === quoteStatus.sent ||
          quote.getStatus() === quoteStatus.declined
        ) {
          this.subscription.add(
            this.quoteRepository.acceptQuote(quote.getId()).subscribe({
              next: (acceptedQuote) => {
                statusLoop(requestIndex++);
              },
              error: (err) => {
                this.sentry.handleError(err, { quoteOnDelete: quote });
                this.notifyService.toast(
                  TOAST.ERROR,
                  "Accept error",
                  new BackendErrorPipe().transform(err),
                );
                statusLoop(requestIndex++);
              },
            }),
          );
        } else {
          statusLoop(requestIndex++);
        }
      }

      if (requestIndex === selected.length) {
        this.isBatchActionCompleted = true;
        this.selectionChange.emit([]);
        this.notifyService.toast(TOAST.INFO, "Batch accept complete");
        this.batchDeleteComplete.emit();
      }
    };

    this.subscription.add(
      this.notifyService
        .confirm("Are you sure you want to accept selected quotes?", "Accept")
        .subscribe({
          next: () => {
            statusLoop(requestIndex);
          },
          error: () => {
            this.isBatchActionCompleted = true;
          },
        }),
    );
  }

  public batchDecline(reasonOfDecline?: string) {
    let requestIndex = 0;
    this.isBatchActionCompleted = false;
    const selected = [...this.selectedQuotes()];

    const statusLoop = (id: number) => {
      if (requestIndex < selected.length) {
        const quote = selected[requestIndex];

        if (
          quote.getStatus() === quoteStatus.sent ||
          quote.getStatus() === quoteStatus.accepted ||
          quote.getStatus() === quoteStatus.expired
        ) {
          this.subscription.add(
            this.quoteRepository
              .declineQuote(quote.getId(), reasonOfDecline)
              .subscribe({
                next: (declinedQuote) => {
                  statusLoop(requestIndex++);
                },
                error: (err) => {
                  this.sentry.handleError(err, { quoteOnDelete: quote });
                  this.notifyService.toast(
                    TOAST.ERROR,
                    "Decline error",
                    new BackendErrorPipe().transform(err),
                  );
                  statusLoop(requestIndex++);
                },
              }),
          );
        } else {
          statusLoop(requestIndex++);
        }
      }

      if (requestIndex === selected.length) {
        this.isBatchActionCompleted = true;
        this.selectionChange.emit([]);
        this.notifyService.toast(TOAST.INFO, "Batch decline complete");
        this.batchDeleteComplete.emit();
      }
    };

    this.subscription.add(
      this.notifyService
        .confirm("Are you sure you want to decline selected quotes?", "Decline")
        .subscribe({
          next: () => {
            statusLoop(requestIndex);
          },
          error: () => {
            this.isBatchActionCompleted = true;
          },
        }),
    );
  }

  public isBatchAcceptDisabled() {
    let result = false;
    this.selectedQuotes().forEach((quote) => {
      if (
        quote.getStatus() !== quoteStatus.sent &&
        quote.getStatus() !== quoteStatus.declined
      ) {
        result = true;
      }
    });

    return result || this.selectedQuotes().length === 0;
  }

  public isBatchDeclineDisabled() {
    let result = false;
    this.selectedQuotes().forEach((quote) => {
      if (
        quote.getStatus() !== quoteStatus.sent &&
        quote.getStatus() !== quoteStatus.accepted &&
        quote.getStatus() !== quoteStatus.expired
      ) {
        result = true;
      }
    });

    return result || this.selectedQuotes().length === 0;
  }

  public isBatchDeleteDisabled() {
    let result = false;
    this.selectedQuotes().forEach((quote) => {
      if (quote.getStatus() !== quoteStatus.draft) {
        result = true;
      }
    });

    return result || this.selectedQuotes().length === 0;
  }

  public isBatchReplicateDisabled() {
    let result = false;
    this.selectedQuotes().forEach((quote) => {
      if (quote.getStatus() === quoteStatus.draft) {
        result = true;
      }
    });

    return result || this.selectedQuotes().length === 0;
  }

  public isBatchEmailDisabled() {
    return this.isBatchDeleteDisabled();
  }

  public emitClearSelection() {
    this.clearSelection.emit();
  }

  private emitRequestInfo(quoteId: number, type: string, msg: string) {
    this.requestInfoChange.emit({
      key: quoteId,
      value: {
        type: type,
        msg: msg,
      },
    });
  }
}
